<script setup>
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { Badge } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useAuditText } from '../../../components/novel/audit/audit-text.js'

const props = defineProps({
  business: { type: Object, required: true },
  compact: { type: Boolean, default: false },
})
const { t, te } = useI18n()
const { errorLabel } = useAuditText()
const ns = 'visualGameAdmin.storyCallBusiness'
const errorText = code => te(`${ns}.errors.${code}`) ? t(`${ns}.errors.${code}`) : errorLabel(code)
const variant = computed(() => ({ succeeded: 'success', recovered: 'warning', failed: 'danger',
  processing: 'warning', degraded: 'warning' }[props.business.status] ?? 'neutral'))
const task = computed(() => {
  const value = props.business.task ?? ''
  const base = value.replace(/_repair$/, '')
  const key = `${ns}.tasks.${base}`
  const label = te(key) ? t(key) : value
  return value.endsWith('_repair') ? `${label} · ${t(`${ns}.format_repair`)}` : label
})
const turn = computed(() => {
  const { from_seq, through_seq } = props.business
  if (through_seq === null) return ''
  return from_seq !== null && from_seq < through_seq
    ? t(`${ns}.range`, { from: from_seq, through: through_seq })
    : t(`${ns}.turn`, { count: through_seq })
})
const run_path = computed(() => ({
  path: `/novel/runs/${encodeURIComponent(props.business.run_id)}`,
  query: props.business.branch_id ? { branch_id: props.business.branch_id } : {},
  hash: '#calls',
}))
</script>

<template>
  <section class="story-business" :class="{ 'story-business--compact': compact }" data-testid="story-call-business">
    <header class="story-business__head">
      <h3 v-if="!compact">{{ t(`${ns}.title`) }}</h3>
      <Badge v-else variant="neutral">{{ t(`${ns}.story`) }}</Badge>
      <Badge :variant="variant" data-testid="story-business-status">{{ t(`${ns}.statuses.${business.status}`) }}</Badge>
    </header>
    <RouterLink v-if="business.run_id" :to="run_path" :title="t(`${ns}.open_run`)" data-testid="story-business-run">
      {{ business.pack_title }}
    </RouterLink>
    <p v-else>{{ t(`${ns}.unlinked`) }}</p>
    <p class="story-business__meta">{{ task }}<template v-if="turn"> · {{ turn }}</template></p>
    <p v-if="business.error_code" :title="business.error_code" :class="{ 'vga-error': business.status === 'failed' }" data-testid="story-business-error">
      {{ errorText(business.error_code) }} · <code>{{ business.error_code }}</code>
    </p>
    <template v-if="!compact">
      <p class="story-business__hint">{{ t(`${ns}.hint`) }}</p>
      <p v-if="business.status === 'unknown'" class="story-business__hint">{{ t(`${ns}.unknown_hint`) }}</p>
      <dl class="story-business__facts">
        <div v-if="business.failure_stage">
          <dt>{{ t(`${ns}.failure_stage`) }}</dt>
          <dd>{{ t(`${ns}.stages.${business.failure_stage}`) }}</dd>
        </div>
        <div v-if="business.recovery">
          <dt>{{ t(`${ns}.recovery`) }}</dt>
          <dd>{{ t(`${ns}.recoveries.${business.recovery}`) }}</dd>
        </div>
        <div>
          <dt>{{ t(`${ns}.call_count`) }}</dt>
          <dd>{{ business.call_count }}</dd>
        </div>
        <div v-if="business.request_id">
          <dt>{{ t(`${ns}.request`) }}</dt>
          <dd><code>{{ business.request_id }}</code></dd>
        </div>
        <div v-if="business.job_id">
          <dt>{{ t(`${ns}.job`) }}</dt>
          <dd><code>{{ business.job_id }}</code></dd>
        </div>
      </dl>
      <div v-if="business.previous_errors.length" class="story-business__history">
        <h4>{{ t(`${ns}.previous_errors`) }}</h4>
        <ul><li v-for="code in business.previous_errors" :key="code">{{ errorText(code) }} · <code>{{ code }}</code></li></ul>
      </div>
    </template>
  </section>
</template>

<style scoped>
.story-business { display: grid; gap: var(--drama-space-sm); min-width: 0; }
.story-business__head { display: flex; align-items: center; flex-wrap: wrap; gap: var(--drama-space-sm); }
.story-business h3, .story-business h4, .story-business p { margin: 0; }
.story-business h3 { font-size: var(--drama-font-size-lg); font-weight: var(--drama-font-weight-medium); }
.story-business a { color: var(--drama-color-primary); overflow-wrap: anywhere; }
.story-business p, .story-business dd, .story-business li { overflow-wrap: anywhere; }
.story-business__meta, .story-business__hint, .story-business dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.story-business__facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: var(--drama-space-md); margin: 0; }
.story-business dd { margin: var(--drama-space-xs) 0 0; }
.story-business__history ul { margin: var(--drama-space-sm) 0 0; padding-left: var(--drama-space-lg); }
.story-business--compact { font-size: var(--drama-font-size-sm); }
</style>
